import { defineTour } from '../../types';
import { PERMISSIONS } from './access';

/**
 * T03 — Track, correct and reorder (brief §7).
 *
 * For a seat that reads its own requests (`request.view_own`, as "My requests"
 * is for a requester). The list is `/my/requests` (GET only). The record steps
 * run only on a request the person opened; the guide never picks one. Amend and
 * Reorder are explained only to a seat that may raise requests — a read-only
 * reader is never led towards a change.
 *
 * Copy: `guidance.tours.T03.*` — draft, for native-language review.
 */
const RECORD = {
  routeId: 'tenant.request',
  explainKey: 'guidance.tours.T03.openRequest',
} as const;

export const T03 = defineTour({
  id: 'T03',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T03.title',
  summaryKey: 'guidance.tours.T03.summary',
  eligible: (ctx) => ctx.portal === 'tenant' && ctx.can(PERMISSIONS.requestViewOwn),
  pageRoutes: ['tenant.my-requests', 'tenant.request'],
  steps: [
    {
      id: 't03-filters',
      anchor: 'tenant.requests.filters',
      route: 'tenant.my-requests',
      titleKey: 'guidance.tours.T03.filters.title',
      bodyKey: 'guidance.tours.T03.filters.body',
    },
    {
      id: 't03-list',
      anchor: 'tenant.requests.list',
      titleKey: 'guidance.tours.T03.list.title',
      bodyKey: 'guidance.tours.T03.list.body',
    },
    {
      id: 't03-next-action',
      anchor: 'tenant.request.next-action',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T03.nextAction.title',
      bodyKey: 'guidance.tours.T03.nextAction.body',
    },
    {
      id: 't03-tabs',
      anchor: 'tenant.request.tabs',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T03.tabs.title',
      bodyKey: 'guidance.tours.T03.tabs.body',
    },
    {
      // Amend exists only on a returned request; on any other request the step
      // explains it at the next-action line, where a return's instruction shows.
      id: 't03-amend',
      anchor: ['tenant.request.amend', 'tenant.request.next-action'],
      needsRecord: RECORD,
      when: (ctx) => ctx.can(PERMISSIONS.requestCreate),
      titleKey: 'guidance.tours.T03.amend.title',
      bodyKey: 'guidance.tours.T03.amend.body',
    },
    {
      // A one-press action that creates a draft: explained, never pressed. A
      // draft request has no Reorder yet; on one, the step says so (TOUR-04).
      id: 't03-reorder',
      anchor: 'tenant.request.reorder',
      needsRecord: RECORD,
      absent: {
        present: 'tenant.request.next-action',
        explainKey: 'guidance.tours.T03.reorderAbsent',
      },
      when: (ctx) => ctx.can(PERMISSIONS.requestCreate),
      titleKey: 'guidance.tours.T03.reorder.title',
      bodyKey: 'guidance.tours.T03.reorder.body',
      commitNote: true,
    },
  ],
});
